"use client";

import { useState } from "react";
import { TradeToEarnLeaderboardSchema } from "@cede/types";
import { getLeaderboardQueryKey } from "@cede/utils";
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCampaign, useLeaderboard } from "@/hooks";
import { useQueryClient } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { BarChart3, Plus, Shuffle, TrendingUp, Trash2, Sparkles } from "lucide-react";
import { useParams } from "next/navigation";

const randomHex = () => `0x${Array.from({ length: 40 }, () => Math.floor(Math.random() * 16).toString(16)).join("")}`;
const rand = (min: number, max: number) => Math.random() * (max - min) + min;

const generateRecord = (rank: number, rewardTokenSymbol = "USDT") => {
  const volume = rand(500, 500_000);
  const id = Math.floor(rand(1000, 99999));
  return {
    id,
    leaderboardId: 1,
    hubUserId: id,
    rank,
    volumeUSD: volume,
    tradedVolumeUSD: volume * rand(0.8, 1.0),
    rewardTokenAmount: rand(10, 5000),
    rewardTokenSymbol,
    rewardTokenAddress: randomHex(),
    rewardClaimed: null,
    tradesCount: Math.floor(rand(1, 300)),
    hubUser: {
      id,
      defiAddress: randomHex(),
      createdAt: new Date(),
      isBanned: false,
      isAmbassador: Math.random() < 0.05,
    },
  };
};

export const LeaderboardFeature = () => {
  const [count, setCount] = useState(20);
  const queryClient = useQueryClient();
  const { id: campaignName } = useParams();
  const { currentEpoch } = useCampaign(campaignName as string);
  const { leaderboard } = useLeaderboard(campaignName as string, currentEpoch ?? undefined);

  const epochId = currentEpoch?.id;
  const isActive = !!campaignName && epochId != null;
  const queryKey = isActive ? getLeaderboardQueryKey(campaignName as string, epochId) : null;
  const hasData = !!leaderboard && leaderboard.leaderboardRecords.length > 0;

  const setData = (data: typeof leaderboard) => {
    if (!queryKey) return;
    queryClient.setQueryData(queryKey, data);
  };

  const handleGenerate = () => {
    if (!isActive) return;
    const records = Array.from({ length: count }, (_, i) => generateRecord(i + 1));
    records.sort((a, b) => b.volumeUSD - a.volumeUSD);
    records.forEach((r, i) => {
      r.rank = i + 1;
    });

    // Parse to convert plain numbers into Decimal instances expected by the UI
    const parsed = TradeToEarnLeaderboardSchema.parse({
      id: 1,
      campaign: campaignName,
      epochId,
      rewardVaultAddress: randomHex(),
      cooldownEnd: null,
      leaderboardRecords: records,
    });
    setData(parsed);
  };

  const handleAddSingle = () => {
    if (!leaderboard) return;
    const rewardSymbol = leaderboard.leaderboardRecords[0]?.rewardTokenSymbol ?? "USDT";
    const newRecord = generateRecord(leaderboard.leaderboardRecords.length + 1, rewardSymbol);

    // Re-parse entire leaderboard so the new record gets Decimal fields
    const raw = {
      ...leaderboard,
      leaderboardRecords: [
        ...leaderboard.leaderboardRecords.map((r) => ({
          ...r,
          volumeUSD: r.volumeUSD.toNumber(),
          tradedVolumeUSD: r.tradedVolumeUSD.toNumber(),
          rewardTokenAmount: r.rewardTokenAmount.toNumber(),
        })),
        newRecord,
      ],
    };
    raw.leaderboardRecords.sort((a, b) => b.volumeUSD - a.volumeUSD);
    raw.leaderboardRecords.forEach((r, i) => {
      r.rank = i + 1;
    });
    setData(TradeToEarnLeaderboardSchema.parse(raw));
  };

  const handleShuffle = () => {
    if (!leaderboard || !hasData) return;
    const records = [...leaderboard.leaderboardRecords];
    for (let i = records.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [records[i], records[j]] = [records[j]!, records[i]!];
    }
    setData({ ...leaderboard, leaderboardRecords: records.map((r, i) => ({ ...r, rank: i + 1 })) });
  };

  const handleSimulateUpdate = () => {
    if (!leaderboard || !hasData) return;
    const records = leaderboard.leaderboardRecords.map((r) => {
      const delta = (Math.random() - 0.3) * 20_000;
      return {
        ...r,
        volumeUSD: new Decimal(Math.max(100, r.volumeUSD.toNumber() + delta)),
        tradedVolumeUSD: new Decimal(Math.max(80, r.tradedVolumeUSD.toNumber() + delta * Math.random())),
        rewardTokenAmount: new Decimal(r.rewardTokenAmount.toNumber() + Math.random() * 200),
        tradesCount: r.tradesCount + Math.floor(Math.random() * 10),
      };
    });
    records.sort((a, b) => b.volumeUSD.toNumber() - a.volumeUSD.toNumber());
    records.forEach((r, i) => {
      r.rank = i + 1;
    });
    setData({ ...leaderboard, leaderboardRecords: records });
  };

  const handleClear = () => {
    if (!leaderboard) return;
    setData({ ...leaderboard, leaderboardRecords: [] });
  };

  const statusText = isActive ? `Trade: ${campaignName} (Epoch ${epochId})` : "No leaderboard on this page";

  return (
    <AccordionItem
      value="leaderboard"
      className="rounded-large border-container bg-cede-bg-component/60 overflow-hidden"
    >
      <AccordionTrigger className="px-4 py-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          <span className="font-semibold">Leaderboard</span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 py-3 space-y-3">
        <div className="text-xs text-cede-text-secondary bg-cede-bg-primary/50 rounded-md px-2.5 py-1.5">
          {statusText}
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="leaderboard-entries" className="text-xs text-cede-text-secondary whitespace-nowrap">
            Entries:
          </label>
          <Input
            id="leaderboard-entries"
            type="number"
            min={1}
            max={200}
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(200, Number(e.target.value) || 1)))}
            className="h-8 text-xs"
            disabled={!isActive}
          />
        </div>

        <div className="space-y-2">
          <Button
            variant="secondary"
            size="medium"
            className="w-full justify-start"
            onClick={handleGenerate}
            disabled={!isActive}
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Generate {count} Entries
          </Button>

          <Button
            variant="secondary"
            size="medium"
            className="w-full justify-start"
            onClick={handleAddSingle}
            disabled={!isActive || !hasData}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Single Entry
          </Button>

          <Button
            variant="secondary"
            size="medium"
            className="w-full justify-start"
            onClick={handleShuffle}
            disabled={!isActive || !hasData}
          >
            <Shuffle className="h-4 w-4 mr-2" />
            Shuffle Ranks
          </Button>

          <Button
            variant="secondary"
            size="medium"
            className="w-full justify-start"
            onClick={handleSimulateUpdate}
            disabled={!isActive || !hasData}
          >
            <TrendingUp className="h-4 w-4 mr-2" />
            Simulate Volume Update
          </Button>

          <Button
            variant="secondary"
            size="medium"
            className="w-full justify-start text-cede-error"
            onClick={handleClear}
            disabled={!isActive || !hasData}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear Leaderboard
          </Button>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
